import React, {
    ChangeEvent,
    Dispatch,
    forwardRef,
    RefObject,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import { useParams } from 'react-router-dom';
import { useDeviceIdInput } from '@controlant/utility-library';
import ClearIcon from '@mui/icons-material/Clear';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputAdornment, Skeleton, TextField } from '@mui/material';
import { Match, Option, pipe } from 'effect';

import { DATE_AND_TIME_FORMAT_SHORT } from '../AppConstants';
import { clearAndFocusInputRef, formatDateTime } from '../helpers/FormattingHelpers';
import { createLoggerId } from '../helpers/LoggerHelpers';
import { getLoggerLimitFromStore, setLoggerLimitToStore } from '../helpers/ScanningHelpers';
import { isNonNullable, isNullable } from '../helpers/TypingHelpers';
import { ScanType } from '../services/scan-types/schemas';
import { ObjectQueryState } from '../types/Api';
import { LoggerId, LoggerListItem } from '../types/Logger';
import { ScanBoxCommon } from '../types/Scan';
import { PaginatedResponse } from '../types/Table';
import { User } from '../types/User';
import { doesScanBelongToUserLocation, isAnotherUserScanning } from '../views/ScanOrBoxDetail/ScanOrBoxDetail.utils';
import NotificationSection, { NotificationType } from './NotificationSection/NotificationSection';
import { LoggerCountLimit } from './LoggerCountLimit';
import { TOAST_TYPE_MAP, ToastCustom } from './ToastCustom';

interface SearchState {
    searchByLoggerId: string;
    setSearchByLoggerId: Dispatch<SetStateAction<string>>;
    searchRef: RefObject<HTMLInputElement | null>;
    onSearchChange: (searchVal: ChangeEvent<HTMLInputElement>) => void;
}

interface LoggerListState {
    loggerList: Array<LoggerListItem>;
    setLoggerList: Dispatch<SetStateAction<Array<LoggerListItem>>>;
    isLoggerCheckPending: boolean;
}

interface LoggerLimitState {
    setLoggerMinLimitErr: Dispatch<SetStateAction<boolean>>;
    setLoggerMaxLimitErr: Dispatch<SetStateAction<boolean>>;
}

interface Props {
    scanOrBox: ScanBoxCommon;
    scanTypesResData: ObjectQueryState<PaginatedResponse<ScanType>>;
    currentUser: User;
    isBatchActionLoading: boolean;
    runLoggerCheckQuery: (loggerIds: Array<LoggerId>) => void;
    loggerListState: LoggerListState;
    loggerLimitState: LoggerLimitState;
    searchState: SearchState;
}

export type InputsRef = {
    clearLimits: () => void;
};

export enum FocusableInputEnum {
    Scan = 'scan-input',
    Search = 'search-input'
}

const ScanOrBoxDetailInputSection = forwardRef<InputsRef, Props>((props, ref) => {
    const {
        scanOrBox,
        loggerListState,
        scanTypesResData,
        currentUser,
        isBatchActionLoading,
        runLoggerCheckQuery,
        loggerLimitState,
        searchState
    } = props;

    const loggerIdInputRef = useRef<HTMLInputElement>(null);
    const { scanId, boxId } = useParams<{ scanId: string; boxId: string }>();
    const scanTypes = scanTypesResData.data?.items || [];

    const [minCountLimit, setMinCountLimit] = useState<number | undefined>(() =>
        pipe(
            Option.fromNullable(getLoggerLimitFromStore('min', scanId, boxId)),
            Option.map((limit) => parseInt(limit, 10)),
            Option.getOrUndefined
        )
    );
    const [maxCountLimit, setMaxCountLimit] = useState<number | undefined>(() =>
        pipe(
            Option.fromNullable(getLoggerLimitFromStore('max', scanId, boxId)),
            Option.map((limit) => parseInt(limit, 10)),
            Option.getOrUndefined
        )
    );

    const { handleInputChange } = useDeviceIdInput({
        onValidId: async (formattedId) => {
            const validatedId = createLoggerId(formattedId);

            // Check if we've added the max number of loggers to the scan/box
            const loggerCountMaxLimit = getLoggerLimitFromStore('max', scanId, boxId);
            if (loggerCountMaxLimit && loggerListState.loggerList.length >= parseInt(loggerCountMaxLimit, 10)) {
                ToastCustom(
                    `You have already added the max number of loggers to this ${boxId ? 'box' : 'scan'}`,
                    TOAST_TYPE_MAP.ERROR
                );
                return;
            }

            const loggerAlreadyAdded = loggerListState.loggerList.some((elem) => elem.loggerId === validatedId);
            if (loggerAlreadyAdded) {
                ToastCustom(`Logger ${validatedId} is already in the scan`, TOAST_TYPE_MAP.WARNING, '', validatedId);
                return;
            }

            loggerListState.setLoggerList((prevState) => [
                { loggerId: validatedId, masterStatus: 'pending', customer: {} },
                ...prevState
            ]);
            runLoggerCheckQuery([validatedId]);
        },
        onInvalidId: (reason) => ToastCustom(reason, TOAST_TYPE_MAP.ERROR),
        onSettled: () => clearAndFocusInputRef(loggerIdInputRef)
    });

    useEffect(() => {
        const onSearchEventTrigger = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key === 'f') {
                event.preventDefault();
                searchState.searchRef.current?.select();
            }
        };
        addEventListener('keydown', onSearchEventTrigger);
        return function cleanUp() {
            removeEventListener('keydown', onSearchEventTrigger);
        };
    }, []);

    useEffect(() => {
        if (isNonNullable(minCountLimit)) {
            loggerLimitState.setLoggerMinLimitErr(minCountLimit > loggerListState.loggerList.length);
        }
        if (maxCountLimit) {
            loggerLimitState.setLoggerMaxLimitErr(maxCountLimit < loggerListState.loggerList.length);
        }
    }, [loggerListState.loggerList.length]);

    const userCanAddLoggers = canUserAddLoggersToScan(scanOrBox, currentUser, scanTypes);

    const autoFocusInput = Match.value(userCanAddLoggers).pipe(
        Match.withReturnType<FocusableInputEnum.Scan | FocusableInputEnum.Search>(),
        // We assume that users that can edit the scan or box will be scanning loggers
        Match.when(true, () => FocusableInputEnum.Scan),
        // Users that can't edit the scan or box will be searching for loggers
        Match.when(false, () => FocusableInputEnum.Search),
        Match.exhaustive
    );

    useEffect(() => {
        if (autoFocusInput === FocusableInputEnum.Scan && loggerIdInputRef.current) {
            loggerIdInputRef.current.focus();
        }
    }, [autoFocusInput]);

    useImperativeHandle(ref, () => ({
        clearLimits() {
            setMinCountLimit(undefined);
            setMaxCountLimit(undefined);
        }
    }));

    const anotherUserWarning = isAnotherUserScanning(scanOrBox, currentUser);
    const isScanTypeDataReady = scanTypesResData.data && !scanTypesResData.isLoading;
    const cantModifyData = getCantModifyData(scanOrBox, boxId, anotherUserWarning, scanTypes);
    const getCantModifySection = cantModifyData ? (
        <NotificationSection type={cantModifyData.severity} mainContent={cantModifyData.message} />
    ) : null;

    const inputOrNotificationContent = (
        <Box display="flex" flexDirection="row" gap="16px" alignItems="center" flexWrap="wrap">
            {!isScanTypeDataReady && (
                <Skeleton data-testid="input-skeleton" variant="rounded" height={40} width="520px" />
            )}

            {userCanAddLoggers && isScanTypeDataReady && (
                <>
                    <TextField
                        inputRef={loggerIdInputRef}
                        onChange={(e) => handleInputChange(e.target.value)}
                        disabled={isBatchActionLoading}
                        autoFocus={autoFocusInput === FocusableInputEnum.Scan}
                        placeholder="Scan Logger ID"
                        sx={{ width: '240px', paddingRight: 0 }}
                        InputLabelProps={{
                            shrink: true
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <DocumentScannerIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        size="small"
                    />

                    <LoggerCountLimit
                        minCountLimit={minCountLimit}
                        maxCountLimit={maxCountLimit}
                        setMinCountLimit={(limit) => {
                            setMinCountLimit(limit);
                            setLoggerLimitToStore('min', limit, scanId, boxId);
                            if (limit !== undefined) {
                                loggerLimitState.setLoggerMinLimitErr(limit > loggerListState.loggerList.length);
                            }
                        }}
                        setMaxCountLimit={(limit) => {
                            setMaxCountLimit(limit);
                            setLoggerLimitToStore('max', limit, scanId, boxId);
                            if (limit !== undefined) {
                                loggerLimitState.setLoggerMaxLimitErr(limit < loggerListState.loggerList.length);
                            }
                        }}
                        isDisabled={isBatchActionLoading}
                    />
                </>
            )}

            {!userCanAddLoggers && isScanTypeDataReady && getCantModifySection}
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: userCanAddLoggers ? '35px' : '20px'
            }}
        >
            {inputOrNotificationContent}

            <TextField
                value={searchState.searchByLoggerId}
                onChange={searchState.onSearchChange}
                placeholder="Search by Logger ID"
                inputRef={searchState.searchRef}
                sx={{ width: '240px' }}
                autoFocus={autoFocusInput === FocusableInputEnum.Search}
                InputProps={{
                    'aria-label': 'Search by "Logger ID"',
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: searchState.searchByLoggerId ? (
                        <InputAdornment position="end">
                            <IconButton onClick={() => searchState.setSearchByLoggerId('')} edge="end">
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    ) : null
                }}
                size="small"
            />
        </Box>
    );
});

export default ScanOrBoxDetailInputSection;

// TODO: SS-526 - Verify that delete message is displayed for BOX when API has been updated to return deletedByUsername
export const getCantModifyData = (
    scanOrBox: ScanBoxCommon | undefined,
    boxId: string | undefined,
    isAnotherUserScanning: boolean,
    scanTypes: Array<ScanType>
): { message: string; severity: NotificationType } | null => {
    const isScanItemFound = scanTypes.some((t) => t.name === scanOrBox?.scanTypeName);

    if (scanOrBox?.deletedByUsername) {
        const deletedTime = formatDateTime(scanOrBox.deletedTime, DATE_AND_TIME_FORMAT_SHORT);
        return {
            message: `This ${boxId ? 'box' : 'scan'} was deleted by ${scanOrBox.deletedByUsername} at ${deletedTime} — modifications are disabled`,
            severity: NotificationType.WARNING
        };
    } else if (!isScanItemFound) {
        return {
            message: `This ${boxId ? 'box' : 'scan'} has an unknown type. Modifications are disabled`,
            severity: NotificationType.ERROR
        };
    } else if (scanOrBox?.statusName === 'COMPLETE') {
        return {
            message: `This ${boxId ? 'box' : 'scan'} is complete — modifications are disabled`,
            severity: NotificationType.SUCCESS
        };
    } else if (isAnotherUserScanning) {
        return {
            message: `This ${boxId ? 'box' : 'scan'} was created by another user — modifications are disabled`,
            severity: NotificationType.WARNING
        };
    }

    return null;
};

const canUserAddLoggersToScan = (scanItem: ScanBoxCommon, currentUser: User, scanTypes: Array<ScanType>) => {
    const isScanTypeFound = scanTypes.some((t) => t.name === scanItem.scanTypeName);

    return (
        scanItem.statusName !== 'COMPLETE' &&
        isNullable(scanItem.deletedTime) &&
        !isAnotherUserScanning(scanItem, currentUser) &&
        doesScanBelongToUserLocation(scanItem, currentUser) &&
        isScanTypeFound
    );
};
